// Enriches the raw sampleData array with the new keys required for inline
// editing — values that were previously hardcoded inside JSX. After seeding,
// the returned array IS the single source of truth for the renderer; any
// field that needs editing can be addressed via dataPaths.

const p = (s) => `<p>${s}</p>`;

// Convert a "\n\n"-separated plain text blob into <p>...</p> HTML.
function paragraphsToHtml(text) {
  if (text == null) return '';
  return text
    .split(/\n\s*\n/)
    .map(part => part.trim())
    .filter(Boolean)
    .map(part => `<p>${part.replace(/\n/g, '<br/>')}</p>`)
    .join('');
}

function htmlList(items) {
  return `<ul>${items.map(i => `<li>${i}</li>`).join('')}</ul>`;
}

const HOW_TO_USE_TIPS_DEFAULT = [
  'Review the results with a mindset of curiosity and learning, not evaluation.',
  'Pay attention to themes across groups rather than single comments or scores.',
  'Identify strengths to leverage — the competencies you demonstrate most often.',
  'Note opportunities to grow — behaviors that, if shown more consistently, could increase impact.',
  'Translate insights into 1–2 specific next steps that align with your goals and the ALC pillars.',
];

const RESULTS_SUMMARY_BULLETS_DEFAULT = [
  'The <strong>colors within the circumplex</strong> depict your &lsquo;All but Self&rsquo; score.',
  'The <strong>solid line</strong> depicts the &lsquo;Self&rsquo; average scores.',
  'The <strong>dotted line</strong> represents the &lsquo;Norm&rsquo;.',
];

export function seedConfigFromSample(rawData) {
  return rawData.map(entry => {
    switch (entry.pageContent) {
      case 'firstPage': {
        const tc = entry.tableContents || {};
        const contents = (tc.contents || []).map(row => {
          if (row.length < 4) return row;
          const next = row.slice();
          // Wrap description cell as HTML if it isn't already
          if (typeof next[3] === 'string' && !/<\w+/.test(next[3])) {
            next[3] = p(next[3].replace(/\n/g, '<br/>'));
          }
          return next;
        });
        return {
          ...entry,
          confidentialLabel: entry.confidentialLabel || p('CONFIDENTIAL'),
          responseRateHeading: entry.responseRateHeading || p('RESPONSE RATE'),
          othersNote:
            entry.othersNote ||
            p('<strong>Others</strong>: If any of these rater categories (&ldquo;Peer&rdquo; and &ldquo;Team Member&rdquo;) have less than 3 respondents, the results are combined and displayed as &ldquo;Others&rdquo; to maintain confidentiality of responses. Hence, a minimum of 3 respondents are required in these categories for results to be displayed independently.'),
          nominatedHeading: entry.nominatedHeading || p('YOUR NOMINATED FEEDBACK PROVIDERS'),
          tableContents: { ...tc, contents },
        };
      }
      case 'aboutPage': {
        return {
          ...entry,
          sectionHeading: entry.sectionHeading || p('ABOUT THIS REPORT'),
          seekPerspectiveReports:
            (typeof entry.seekPerspectiveReports === 'string' && /<\w+/.test(entry.seekPerspectiveReports))
              ? entry.seekPerspectiveReports
              : paragraphsToHtml(entry.seekPerspectiveReports || ''),
          calloutHeadline:
            entry.calloutHeadline ||
            p('The 360 process is about seeking perspective and building awareness.'),
          calloutBody:
            entry.calloutBody ||
            p('Everyone has areas of strength and areas of opportunity — this report simply helps you see them more clearly so you can keep progressing on your leadership journey.'),
          leversHeading: entry.leversHeading || p('4 LEVERS FOR MANAGEMENT EXCELLENCE'),
          leversSubtext:
            entry.leversSubtext ||
            p('Leadership is a human experience for everyone. We engage the whole person to develop and coach them to achieve their goals and do their best work.'),
        };
      }
      case 'outlineOfThisReport': {
        return {
          ...entry,
          sectionHeading: entry.sectionHeading || p('OUTLINE OF THIS REPORT'),
          howToUseHeading: entry.howToUseHeading || p('How to Use This Report'),
          howToUseTips: entry.howToUseTips || htmlList(HOW_TO_USE_TIPS_DEFAULT),
        };
      }
      case 'circumplexData': {
        return {
          ...entry,
          sectionHeading: entry.sectionHeading || p('RESULTS SUMMARY'),
          intro:
            entry.intro ||
            p('This circumplex provides a visual representation of your results based on &lsquo;All But Self&rsquo; scores i.e. average scores for all feedback provider groups <strong>except</strong> for &lsquo;Self&rsquo;. The scores are shown on a 6-point scale.'),
          interpretLead: entry.interpretLead || p('To interpret the circumplex, note the following:'),
          interpretBullets: entry.interpretBullets || htmlList(RESULTS_SUMMARY_BULLETS_DEFAULT),
        };
      }
      case 'topStrengths': {
        return {
          ...entry,
          sectionHeading: entry.sectionHeading || p('TOP STRENGTHS'),
          topStrengthsReport:
            (typeof entry.topStrengthsReport === 'string' && /<\w+/.test(entry.topStrengthsReport))
              ? entry.topStrengthsReport
              : paragraphsToHtml(entry.topStrengthsReport || ''),
          topStrengthsContent:
            (typeof entry.topStrengthsContent === 'string' && /<\w+/.test(entry.topStrengthsContent))
              ? entry.topStrengthsContent
              : paragraphsToHtml(entry.topStrengthsContent || ''),
        };
      }
      case 'strenghtComments': {
        return {
          ...entry,
          sectionHeading: entry.sectionHeading || p('EXAMPLES OF STRENGTHS'),
          pageConstants:
            (typeof entry.pageConstants === 'string' && /<\w+/.test(entry.pageConstants))
              ? entry.pageConstants
              : paragraphsToHtml(entry.pageConstants || ''),
          sectionHeaders: entry.sectionHeaders || {
            self: p('SELF COMMENTS'),
            manager: p('MANAGER COMMENTS'),
            team: p('TEAM MEMBER COMMENTS'),
            peer: p('PEER COMMENTS'),
          },
        };
      }
      case 'keyDevelopmentAreas': {
        return {
          ...entry,
          sectionHeading: entry.sectionHeading || p('TOP DEVELOPMENT AREAS'),
          pagePara:
            (typeof entry.pagePara === 'string' && /<\w+/.test(entry.pagePara))
              ? entry.pagePara
              : paragraphsToHtml(entry.pagePara || ''),
          summaryContent:
            (typeof entry.summaryContent === 'string' && /<\w+/.test(entry.summaryContent))
              ? entry.summaryContent
              : paragraphsToHtml(entry.summaryContent || ''),
        };
      }
      case 'devlopmentAreasComments': {
        return {
          ...entry,
          sectionHeading: entry.sectionHeading || p('DEVELOPMENT AREAS COMMENTS'),
          pageConstants:
            (typeof entry.pageConstants === 'string' && /<\w+/.test(entry.pageConstants))
              ? entry.pageConstants
              : paragraphsToHtml(entry.pageConstants || ''),
          sectionHeaders: entry.sectionHeaders || {
            self: p('SELF COMMENTS'),
            manager: p('MANAGER COMMENTS'),
            team: p('TEAM MEMBER COMMENTS'),
            peer: p('PEER COMMENTS'),
          },
        };
      }
      case 'twoByTwoMatrix': {
        const bulletItems = (entry.twoByTwoBullets || []).map(b => {
          if (typeof b === 'string' && /<\w+/.test(b)) return b;
          const parts = (b || '').split(' - ');
          if (parts.length >= 2) {
            return `<strong>${parts[0]} -</strong> ${parts.slice(1).join(' - ')}`;
          }
          return b || '';
        });
        return {
          ...entry,
          sectionHeading: entry.sectionHeading || p('2X2 MATRIX'),
          twoByTwoContent:
            (typeof entry.twoByTwoContent === 'string' && /<\w+/.test(entry.twoByTwoContent))
              ? entry.twoByTwoContent
              : paragraphsToHtml(entry.twoByTwoContent || ''),
          twoByTwoBullets:
            typeof entry.twoByTwoBullets === 'string'
              ? entry.twoByTwoBullets
              : htmlList(bulletItems),
        };
      }
      case 'wordsOfWisdom': {
        return {
          ...entry,
          sectionHeading: entry.sectionHeading || p('WORDS OF WISDOM'),
          sectionSubtitle: entry.sectionSubtitle || p('(Advice for increasing your effectiveness)'),
          introLead:
            entry.introLead ||
            p('Following are the comments that feedback providers gave in response to the question:'),
          question:
            entry.question ||
            p('<strong>What would be your one piece of advice for this leader to improve their effectiveness?</strong>'),
          sectionHeaders: entry.sectionHeaders || {
            manager: p('MANAGER COMMENTS'),
            team: p('TEAM MEMBER COMMENTS'),
            peer: p('PEER COMMENTS'),
          },
        };
      }
      case 'ratingsOnEachBehavior': {
        return {
          ...entry,
          sectionHeading: entry.sectionHeading || p('APPENDIX: RATINGS ON EACH BEHAVIOR'),
          pageDetails:
            (typeof entry.pageDetails === 'string' && /<\w+/.test(entry.pageDetails))
              ? entry.pageDetails
              : paragraphsToHtml(entry.pageDetails || ''),
          lowConsensus:
            (typeof entry.lowConsensus === 'string' && /<\w+/.test(entry.lowConsensus))
              ? entry.lowConsensus
              : paragraphsToHtml(entry.lowConsensus || ''),
          asteriskNote:
            entry.asteriskNote ||
            p('NOTE: An asterisk (*) indicates segments with fewer than three responses.'),
          consensusTableHeaders: entry.consensusTableHeaders || {
            left: p('<em>Standard Deviation</em>'),
            right: p('<em>Level of Consensus</em>'),
          },
        };
      }
      case 'standardDeviationSection': {
        return {
          ...entry,
          // dimensionLabel is optional; renderer falls back to dimension.toUpperCase()
          dimensionLabel:
            entry.dimensionLabel || p((entry.dimension || '').toUpperCase()),
        };
      }
      default:
        return entry;
    }
  });
}
